import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_LOCALE, Locale, LOCALE_CODES, isLocale, withLocale } from "./config";

export const LOCALE_PREF_KEY = "beedigit.locale";

/** Maps browser language tags (en-US, uk, pt-BR…) to a supported locale. */
function detectLocale(): Locale | null {
  const tags = typeof navigator !== "undefined"
    ? [...(navigator.languages ?? []), navigator.language].filter(Boolean)
    : [];
  for (const tag of tags) {
    const lower = tag.toLowerCase();
    const base = lower.split("-")[0];
    // Common aliases
    const alias: Record<string, string> = { ua: "uk", in: "hi", he: "ar", fa: "ar" };
    const code = alias[base] ?? base;
    if ((LOCALE_CODES as string[]).includes(code)) return code as Locale;
  }
  return null;
}

/**
 * On the first visit (no stored preference) redirects the visitor to the
 * locale that best matches their browser settings.
 */
export function AutoLocaleRedirect() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith("/admin") || path.startsWith("/login")) return;

    let stored: string | null = null;
    try { stored = localStorage.getItem(LOCALE_PREF_KEY); } catch { /* ignore */ }

    // URL already carries a locale → remember it and stop.
    const seg = path.split("/")[1];
    if (isLocale(seg)) {
      if (stored !== seg) { try { localStorage.setItem(LOCALE_PREF_KEY, seg); } catch { /* ignore */ } }
      return;
    }

    // No locale in the URL → this is the English URL. The explicit URL always
    // decides the language; never redirect away from it.
    void stored; void detectLocale; void navigate; void withLocale; void DEFAULT_LOCALE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return null;
}

export default AutoLocaleRedirect;